import { useLiveQuery } from "dexie-react-hooks";
import { Check, ChevronRight } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { colorHex, toISODate, type Habit } from "@todoer/shared";
import { db } from "../lib/db.js";
import { cmd, dispatch, uuid } from "../lib/sync.js";
import { cx } from "./ui.js";

export async function toggleHabit(habit: Habit, date: string, done: boolean): Promise<void> {
  await dispatch(done ? cmd("habit_uncheck", { habitId: habit.id, date }) : cmd("habit_checkin", { id: uuid(), habitId: habit.id, date }));
}

/** Today's habits as a horizontal row of tappable pills, at the top of the Today view. */
export function HabitsToday() {
  const today = toISODate(new Date());
  const [justDone, setJustDone] = useState<string | null>(null);
  const data = useLiveQuery(async () => {
    const habits = (await db.habits.filter((h) => !h.isArchived).toArray()).sort((a, b) => a.itemOrder - b.itemOrder);
    const done = new Set((await db.habitCheckins.where("date").equals(today).toArray()).map((c) => c.habitId));
    return { habits, done };
  }, [today]);
  if (!data?.habits.length) return null;
  const doneCount = data.habits.filter((h) => data.done.has(h.id)).length;
  return (
    <section className="mb-6" aria-labelledby="habits-h">
      <div className="mb-2 flex items-center gap-2">
        <h2 id="habits-h" className="text-caption font-semibold uppercase text-text-faint">Habits</h2>
        <span className="tabular text-meta text-text-faint">{doneCount}/{data.habits.length}</span>
        <Link to="/habits" className="ml-auto inline-flex items-center gap-0.5 rounded-control px-1.5 py-1 text-meta font-medium text-text-muted hover:bg-hover hover:text-text">Manage<ChevronRight size={14} aria-hidden /></Link>
      </div>
      <div className="no-scrollbar -mx-4 flex gap-2 overflow-x-auto px-4 pb-1 md:mx-0 md:flex-wrap md:px-0">
        {data.habits.map((h) => {
          const done = data.done.has(h.id);
          const color = colorHex(h.color);
          return (
            <button key={h.id} type="button" aria-pressed={done}
              onClick={() => { if (!done) setJustDone(h.id); void toggleHabit(h, today, done); }}
              className={cx("flex h-9 shrink-0 items-center gap-2 rounded-full border pl-1.5 pr-3.5 text-body font-medium transition-[background-color,border-color,color] duration-200 phone:h-11",
                done ? "border-transparent text-text" : "border-border bg-surface-raised text-text hover:border-border-strong")}
              style={done ? { background: `color-mix(in srgb, ${color} 16%, transparent)` } : undefined}>
              <span className={cx("grid h-6 w-6 place-items-center rounded-full text-[13px] transition-colors duration-200", done ? "text-white" : "bg-surface-sunken")}
                style={done ? { background: color } : undefined}>
                {done ? <Check size={14} strokeWidth={3} aria-hidden className={justDone === h.id ? "anim-check" : undefined} /> : <span aria-hidden>{h.icon}</span>}
              </span>
              {h.name}
            </button>
          );
        })}
      </div>
    </section>
  );
}
